import React, { useCallback, useEffect, useState } from 'react';
import { getCookie } from '../utils/utils';

interface PendingInvite {
  id: number;
  org: number;
  org_name: string;
  role_name: string;
}

/**
 * Cross-organization invites are not applied until this account confirms them.
 */
const PendingOrgInvites: React.FC = () => {
  const [invites, setInvites] = useState<PendingInvite[]>([]);

  const load = useCallback(async () => {
    try {
      const response = await fetch('/api/org-members/invites/', { credentials: 'include' });
      if (!response.ok) return;
      const data = await response.json();
      setInvites(Array.isArray(data) ? data : []);
    } catch {
      setInvites([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const respond = async (inviteId: number, action: 'accept' | 'decline') => {
    const csrfToken = getCookie('csrftoken');
    const response = await fetch(`/api/org-members/invites/${inviteId}/respond/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken || '',
      },
      body: JSON.stringify({ action }),
    });
    if (response.ok && action === 'accept') {
      window.location.reload();
      return;
    }
    void load();
  };

  if (invites.length === 0) return null;

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-3">
      {invites.map((invite) => (
        <div key={invite.id} className="flex flex-wrap items-center justify-between gap-3 py-1">
          <p className="text-sm text-amber-950">
            {invite.org_name} invited you{invite.role_name ? ` as ${invite.role_name}` : ''}. Confirm to join.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md bg-amber-700 px-3 py-1 text-sm text-white"
              onClick={() => void respond(invite.id, 'accept')}
            >
              Accept
            </button>
            <button
              type="button"
              className="rounded-md border border-amber-300 bg-white px-3 py-1 text-sm text-amber-950"
              onClick={() => void respond(invite.id, 'decline')}
            >
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PendingOrgInvites;
